import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from '@apollo/client';
import { GET_FINANCIAL_DATA } from '../../graphql/queries/financialData';
import PLTab from './PLTab';
import BalanceSheetTab from './BalanceSheetTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`financial-tabpanel-${index}`}
      aria-labelledby={`financial-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
};

interface FinancialDataModalProps {
  open: boolean;
  onClose: () => void;
  companyId: number;
  companyName?: string;
}

const FinancialDataModal: React.FC<FinancialDataModalProps> = ({ open, onClose, companyId, companyName }) => {
  const [tabValue, setTabValue] = useState(0);
  const [plData, setPLData] = useState<any[]>([]);
  const [balanceSheetData, setBalanceSheetData] = useState<any[]>([]);
  const [selectedValuationLevel, setSelectedValuationLevel] = useState<number | undefined>(undefined);
  const [availableValuationLevels, setAvailableValuationLevels] = useState<number[]>([]);

  // Fetch P&L data
  const {
    data: plDataResult,
    loading: plLoading,
    error: plError,
  } = useQuery(GET_FINANCIAL_DATA, {
    variables: {
      companyId,
      bmType: 'pl',
    },
    skip: !open || !companyId,
    fetchPolicy: 'network-only',
  });

  // Fetch Balance Sheet data
  const {
    data: bsDataResult,
    loading: bsLoading,
    error: bsError,
  } = useQuery(GET_FINANCIAL_DATA, {
    variables: {
      companyId,
      bmType: 'balance_sheet',
    },
    skip: !open || !companyId,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (plDataResult?.getFinancialData?.edges) {
      const edges = plDataResult.getFinancialData.edges;
      setPLData(edges);

      // Extract available valuation levels
      const levels = new Set<number>();
      edges.forEach((edge: any) => {
        const valuationLevel = edge.node.valuationLevel ?? 0;
        levels.add(valuationLevel);
      });
      const levelArray = Array.from(levels).sort((a, b) => b - a); // Sort descending (highest first)
      setAvailableValuationLevels(levelArray);

      // Set default to highest available level if none selected
      if (selectedValuationLevel === undefined && levelArray.length > 0) {
        setSelectedValuationLevel(levelArray[0]);
      }
    }
  }, [plDataResult, selectedValuationLevel]);

  useEffect(() => {
    if (bsDataResult?.getFinancialData?.edges) {
      setBalanceSheetData(bsDataResult.getFinancialData.edges);
    }
  }, [bsDataResult]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleValuationLevelChange = (event: SelectChangeEvent<number>) => {
    const value = event.target.value;
    setSelectedValuationLevel(value === 'all' ? undefined : Number(value));
  };

  const isLoading = plLoading || bsLoading;
  const hasError = plError || bsError;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth sx={{ '& .MuiDialog-paper': { height: '90vh' } }}>
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Financial Data {companyName ? `- ${companyName}` : ''}</Typography>
          <Box display="flex" alignItems="center" gap={2}>
            {availableValuationLevels.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Valuation Level</InputLabel>
                <Select
                  value={selectedValuationLevel ?? 'all'}
                  label="Valuation Level"
                  onChange={handleValuationLevelChange}
                >
                  <MenuItem value="all">All Levels</MenuItem>
                  {availableValuationLevels.map(level => (
                    <MenuItem key={level} value={level}>
                      Level {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
            <CircularProgress />
          </Box>
        ) : hasError ? (
          <Alert severity="error">Error loading financial data: {plError?.message || bsError?.message}</Alert>
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="financial data tabs">
                <Tab label="P&L" id="financial-tab-0" aria-controls="financial-tabpanel-0" />
                <Tab label="Balance Sheet" id="financial-tab-1" aria-controls="financial-tabpanel-1" />
              </Tabs>
            </Box>
            <TabPanel value={tabValue} index={0}>
              <PLTab data={plData} companyId={companyId} selectedValuationLevel={selectedValuationLevel} />
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <BalanceSheetTab
                data={balanceSheetData}
                companyId={companyId}
                selectedValuationLevel={selectedValuationLevel}
              />
            </TabPanel>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FinancialDataModal;
